import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  Search,
  Filter,
  Loader2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Mic,
  User,
  Calendar,
  TrendingDown,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { CallRecordingPlayer, RecordingBadge } from "../components/CallRecordingPlayer";
import { useAuth } from "../App";
import { Download } from "lucide-react";

// Admin-only export control: date range + agent + status + call type → downloads Excel
const ExportCallsControls = ({ filters }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [exp, setExp] = useState({ start_date: "", end_date: "", caller_id: "", status: "", call_type: "", lead_date_filter: "none", lead_date_start: "", lead_date_end: "" });

  useEffect(() => {
    if (['admin','super_admin'].includes(user?.role)) {
      // default last 30 days
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setExp((e) => ({
        ...e,
        start_date: start.toISOString().slice(0, 10),
        end_date: end.toISOString().slice(0, 10),
      }));
      axios.get(`${API}/users`).then((r) => setAgents(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  const handleDownload = async () => {
    // Validate optional lead date filter
    if (exp.lead_date_filter !== "none") {
      if (!exp.lead_date_start && !exp.lead_date_end) {
        toast.error("Pick at least a Start or End date for the lead filter, or set it to 'None'");
        return;
      }
      if (exp.lead_date_start && exp.lead_date_end && exp.lead_date_start > exp.lead_date_end) {
        toast.error("Lead filter: Start date must be on or before End date");
        return;
      }
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (exp.start_date) params.append("start_date", exp.start_date);
      if (exp.end_date) params.append("end_date", exp.end_date);
      if (exp.caller_id) params.append("caller_id", exp.caller_id);
      if (exp.status) params.append("status", exp.status);
      if (exp.call_type) params.append("call_type", exp.call_type);
      if (exp.lead_date_filter !== "none") {
        params.append("lead_date_filter", exp.lead_date_filter);
        if (exp.lead_date_start) params.append("lead_date_start", exp.lead_date_start);
        if (exp.lead_date_end) params.append("lead_date_end", exp.lead_date_end);
      }
      const res = await axios.get(`${API}/calls/export-excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `call_history_${exp.start_date}_to_${exp.end_date}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Call history exported");
      setOpen(false);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("No calls found for the selected filters");
      } else {
        toast.error(err.response?.data?.detail || "Failed to export call history");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!["admin", "super_admin"].includes(user?.role)) return null;

  return (
    <>
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-calls-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Call History
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="export-calls-dialog">
          <DialogHeader>
            <DialogTitle>Download Call History</DialogTitle>
            <DialogDescription>Pick filters and export to Excel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={exp.start_date} onChange={(e) => setExp({ ...exp, start_date: e.target.value })} data-testid="export-start-date" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={exp.end_date} onChange={(e) => setExp({ ...exp, end_date: e.target.value })} data-testid="export-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Agent (optional)</Label>
              <Select value={exp.caller_id || "_all_"} onValueChange={(v) => setExp({ ...exp, caller_id: v === "_all_" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="export-agent-select"><SelectValue placeholder="All agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all_">All agents</SelectItem>
                  {agents.filter((a) => a.is_active).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Call Status</Label>
                <Select value={exp.status || "_all_"} onValueChange={(v) => setExp({ ...exp, status: v === "_all_" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="export-status-select"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All</SelectItem>
                    <SelectItem value="connected">Connected</SelectItem>
                    <SelectItem value="not_connected">Not Connected</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="no_answer">No Answer</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="voicemail">Voicemail</SelectItem>
                    <SelectItem value="requirement_shared">Requirement Shared</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Call Type</Label>
                <Select value={exp.call_type || "_all_"} onValueChange={(v) => setExp({ ...exp, call_type: v === "_all_" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="export-type-select"><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All</SelectItem>
                    <SelectItem value="click_to_call">Click-to-Call</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lead-based date filter (optional) */}
            <div className="pt-2 border-t border-slate-200 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Additional Lead-based filter (optional)</Label>
              <Select value={exp.lead_date_filter} onValueChange={(v) => setExp({ ...exp, lead_date_filter: v, ...(v === "none" ? { lead_date_start: "", lead_date_end: "" } : {}) })}>
                <SelectTrigger className="h-9" data-testid="export-lead-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="created">Lead Created Date</SelectItem>
                  <SelectItem value="activity">Lead Last Activity Date</SelectItem>
                </SelectContent>
              </Select>
              {exp.lead_date_filter !== "none" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Lead From</Label>
                    <Input type="date" value={exp.lead_date_start} onChange={(e) => setExp({ ...exp, lead_date_start: e.target.value })} data-testid="export-lead-filter-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Lead To</Label>
                    <Input type="date" value={exp.lead_date_end} onChange={(e) => setExp({ ...exp, lead_date_end: e.target.value })} data-testid="export-lead-filter-end" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={downloading}>Cancel</Button>
            <Button onClick={handleDownload} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-calls-confirm">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CallHistory = () => {
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    days: "7", // Default to last 7 days
    hasRecording: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: "",
    outcome: "",
    notes: "",
    duration_seconds: 0
  });
  
  // Agent call summary for default view
  const [agentSummary, setAgentSummary] = useState([]);
  const [viewMode, setViewMode] = useState("summary"); // "summary" or "all"

  const fetchCalls = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.days) params.append("days", filters.days);

      const response = await axios.get(`${API}/calls?${params.toString()}`);
      const callsData = response.data;
      setCalls(callsData);

      // Build agent summary - group calls by agent
      const agentMap = {};
      callsData.forEach(call => {
        const agentId = call.agent_id || 'unknown';
        const agentName = call.agent_name || 'Unknown Agent';
        if (!agentMap[agentId]) {
          agentMap[agentId] = {
            agent_id: agentId,
            agent_name: agentName,
            total_calls: 0,
            completed_calls: 0,
            total_duration: 0,
            calls: []
          };
        }
        agentMap[agentId].total_calls++;
        agentMap[agentId].calls.push(call);
        if (call.status === 'completed') {
          agentMap[agentId].completed_calls++;
        }
        agentMap[agentId].total_duration += call.duration_seconds || 0;
      });
      
      // Sort by total calls descending (most calls first)
      const sortedAgents = Object.values(agentMap).sort((a, b) => b.total_calls - a.total_calls);
      setAgentSummary(sortedAgents);

      // Fetch lead names for each call
      const leadIds = [...new Set(callsData.map(c => c.lead_id))];
      const leadPromises = leadIds.map(id => 
        axios.get(`${API}/leads/${id}`).catch(() => null)
      );
      const leadResponses = await Promise.all(leadPromises);
      const leadsMap = {};
      leadResponses.forEach(res => {
        if (res?.data) {
          leadsMap[res.data.id] = res.data;
        }
      });
      setLeads(leadsMap);
    } catch (error) {
      console.error("Failed to fetch calls:", error);
      toast.error("Failed to load call history");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [filters.status, filters.days]);

  const handleUpdateCall = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/calls/${selectedCall.id}`, updateData);
      toast.success("Call updated successfully");
      setShowUpdateDialog(false);
      fetchCalls();
    } catch (error) {
      toast.error("Failed to update call");
    }
  };

  const openUpdateDialog = (call) => {
    setSelectedCall(call);
    setUpdateData({
      status: call.status,
      outcome: call.outcome || "",
      notes: call.notes || "",
      duration_seconds: call.duration_seconds || 0
    });
    setShowUpdateDialog(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'failed':
      case 'busy':
      case 'no_answer':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      initiated: "bg-blue-50 text-blue-700 border-blue-200",
      ringing: "bg-yellow-50 text-yellow-700 border-yellow-200",
      answered: "bg-purple-50 text-purple-700 border-purple-200",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      connected: "bg-emerald-50 text-emerald-700 border-emerald-200",
      requirement_shared: "bg-green-50 text-green-700 border-green-200",
      call_again_later: "bg-amber-50 text-amber-700 border-amber-200",
      no_requirement: "bg-slate-50 text-slate-600 border-slate-200",
      not_connected: "bg-red-50 text-red-700 border-red-200",
      not_relevant: "bg-rose-50 text-rose-700 border-rose-200",
      failed: "bg-red-50 text-red-700 border-red-200",
      busy: "bg-orange-50 text-orange-700 border-orange-200",
      no_answer: "bg-slate-50 text-slate-700 border-slate-200",
      unknown_caller: "bg-amber-50 text-amber-700 border-amber-200"
    };
    return colors[status] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0m 0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const filteredCalls = calls.filter(call => {
    // Recording filter
    if (filters.hasRecording === "yes" && !call.recording_url) return false;
    if (filters.hasRecording === "no" && call.recording_url) return false;

    if (filters.search) {
      const lead = leads[call.lead_id];
      const searchLower = filters.search.toLowerCase();
      return (
        call.phone_number?.includes(filters.search) ||
        lead?.name?.toLowerCase().includes(searchLower) ||
        lead?.company_name?.toLowerCase().includes(searchLower) ||
        call.agent_name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Call History
          </h1>
          <p className="text-slate-500 mt-1">
            {calls.length} calls in last {filters.days === 'all' ? 'all' : filters.days} days
            {calls.filter(c => c.recording_url).length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-indigo-600 font-medium">
                <Mic className="w-3 h-3" />
                {calls.filter(c => c.recording_url).length} with recordings
              </span>
            )}
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50">
          <button
            onClick={() => setViewMode("summary")}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === "summary" 
                ? "bg-white shadow text-slate-900 font-medium" 
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="view-summary"
          >
            <TrendingDown className="w-4 h-4 inline mr-2" />
            Agent Summary
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === "all" 
                ? "bg-white shadow text-slate-900 font-medium" 
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="view-all"
          >
            <Phone className="w-4 h-4 inline mr-2" />
            All Calls
          </button>
        </div>
      </div>

      {/* Export to Excel (Admin only) */}
      <ExportCallsControls filters={filters} />

      {/* Search and Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by phone, lead name, company, or agent..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-calls-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={filters.status || filters.days !== "7" ? "border-blue-600 text-blue-600" : ""}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label>Duration (Days)</Label>
                  <Select
                    value={filters.days}
                    onValueChange={(value) => setFilters({ ...filters, days: value })}
                  >
                    <SelectTrigger data-testid="filter-days-select">
                      <SelectValue placeholder="Select days" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Today</SelectItem>
                      <SelectItem value="3">Last 3 days</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="60">Last 60 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="all">All time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={filters.status || "_all_"}
                    onValueChange={(value) => setFilters({ ...filters, status: value === "_all_" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-status-select">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all_">All statuses</SelectItem>
                      <SelectItem value="initiated">Initiated</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="no_answer">No Answer</SelectItem>
                      <SelectItem value="ringing">Inbound - Ringing</SelectItem>
                      <SelectItem value="unknown_caller">Inbound - Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Recording</Label>
                  <Select
                    value={filters.hasRecording || "_all_"}
                    onValueChange={(value) => setFilters({ ...filters, hasRecording: value === "_all_" ? "" : value })}
                  >
                    <SelectTrigger data-testid="filter-recording-select">
                      <SelectValue placeholder="All calls" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all_">All calls</SelectItem>
                      <SelectItem value="yes">Has Recording</SelectItem>
                      <SelectItem value="no">No Recording</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Summary View (Default) */}
      {viewMode === "summary" && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Calls by Agent
              <span className="text-xs font-normal text-slate-500 ml-2">
                (Sorted by most calls)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : agentSummary.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                No calls found in selected duration
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {agentSummary.map((agent, index) => (
                  <div
                    key={agent.agent_id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`agent-row-${agent.agent_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank Badge */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-slate-200 text-slate-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        #{index + 1}
                      </div>
                      
                      {/* Agent Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{agent.agent_name}</span>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{agent.total_calls}</p>
                          <p className="text-xs text-slate-500">Total Calls</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-semibold text-emerald-600">{agent.completed_calls}</p>
                          <p className="text-xs text-slate-500">Completed</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-medium text-slate-700">{formatDuration(agent.total_duration)}</p>
                          <p className="text-xs text-slate-500">Talk Time</p>
                        </div>
                      </div>
                      
                      {/* Expand Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFilters({ ...filters, search: agent.agent_name });
                          setViewMode("all");
                        }}
                        data-testid={`view-agent-calls-${agent.agent_id}`}
                      >
                        View Calls
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All Calls View */}
      {viewMode === "all" && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredCalls.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                No calls found
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredCalls.map((call) => {
                  const lead = leads[call.lead_id];
                  const isExpanded = expandedCallId === call.id;
                  return (
                    <div
                      key={call.id}
                      className={`hover:bg-slate-50 transition-colors ${call.call_type === 'inbound' || call.direction === 'inbound' ? 'border-l-4 border-l-purple-400' : ''}`}
                      data-testid={`call-row-${call.id}`}
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedCallId(isExpanded ? null : call.id)}
                      >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          call.call_type === 'inbound' || call.direction === 'inbound' 
                            ? (call.status === 'unknown_caller' ? 'bg-amber-100' : 'bg-purple-100')
                            : call.status === 'completed' ? 'bg-emerald-100' 
                            : ['failed', 'busy', 'no_answer'].includes(call.status) ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          {call.call_type === 'inbound' || call.direction === 'inbound' ? (
                            <PhoneIncoming className={`w-5 h-5 ${call.status === 'unknown_caller' ? 'text-amber-600' : 'text-purple-600'}`} />
                          ) : (
                            <PhoneOutgoing className={`w-5 h-5 ${
                              call.status === 'completed' ? 'text-emerald-600' : 
                              ['failed', 'busy', 'no_answer'].includes(call.status) ? 'text-red-600' : 'text-blue-600'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            {(call.call_type === 'inbound' || call.direction === 'inbound') && (
                              <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium border border-purple-200">
                                Inbound
                              </span>
                            )}
                            {call.call_type === 'manual' && (
                              <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded text-xs font-medium border border-teal-200">
                                Manual
                              </span>
                            )}
                            <span className="font-mono text-sm text-slate-900">{call.phone_number}</span>
                            <span className={`${getStatusColor(call.status)} px-2 py-0.5 rounded text-xs font-medium border capitalize`}>
                              {call.status?.replace('_', ' ')}
                            </span>
                            <RecordingBadge hasRecording={!!call.recording_url} />
                            {call.screenshot_url && (
                              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-medium border border-indigo-200 flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" /> Screenshot
                              </span>
                            )}
                            {call.outcome && (
                              <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs capitalize">
                                {call.outcome?.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          {lead ? (
                            <Link
                              to={`/leads/${lead.id}`}
                              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {lead.name} - {lead.company_name}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          ) : call.company_name ? (
                            <span className="text-sm text-slate-600 mt-1">{call.company_name}{call.contact_name ? ` - ${call.contact_name}` : ''}</span>
                          ) : null}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                            {/* Agent Name */}
                            <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              <User className="w-3 h-3" />
                              {call.agent_name || 'Unknown Agent'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(call.created_at).toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1 font-medium text-slate-700">
                              Duration: {call.duration_seconds > 0 ? formatDuration(call.duration_seconds) : '0s'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openUpdateDialog(call); }}
                            data-testid={`update-call-${call.id}`}
                          >
                            Update
                          </Button>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/50" data-testid={`call-expanded-${call.id}`}>
                          <div className="ml-16 space-y-3">
                            {/* Recording Player */}
                            <CallRecordingPlayer call={call} />
                            {call.notes && (
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Notes</p>
                                <p className="text-sm text-slate-700 bg-white p-2 rounded border border-slate-200">
                                  {call.notes}
                                </p>
                              </div>
                            )}
                            {/* Screenshot */}
                            {call.screenshot_url && (
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Call Screenshot</p>
                                <a href={call.screenshot_url} target="_blank" rel="noopener noreferrer" className="inline-block">
                                  <img
                                    src={call.screenshot_url}
                                    alt="Call screenshot"
                                    className="max-w-sm max-h-64 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                    data-testid={`call-screenshot-${call.id}`}
                                  />
                                </a>
                              </div>
                            )}
                            {!call.notes && !call.screenshot_url && !call.recording_url && (
                              <p className="text-sm text-slate-400 italic">No additional details</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Update Call Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Call</DialogTitle>
            <DialogDescription>Update call details and outcome</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCall} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={updateData.status}
                onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
              >
                <SelectTrigger data-testid="update-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initiated">Initiated</SelectItem>
                  <SelectItem value="ringing">Ringing</SelectItem>
                  <SelectItem value="answered">Answered</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select
                value={updateData.outcome}
                onValueChange={(value) => setUpdateData({ ...updateData, outcome: value })}
              >
                <SelectTrigger data-testid="update-outcome-select">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="not_interested">Not Interested</SelectItem>
                  <SelectItem value="callback">Callback Requested</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="wrong_number">Wrong Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duration (seconds)</Label>
              <Input
                type="number"
                value={updateData.duration_seconds}
                onChange={(e) => setUpdateData({ ...updateData, duration_seconds: parseInt(e.target.value) || 0 })}
                min="0"
                data-testid="update-duration-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                rows={3}
                placeholder="Add call notes..."
                data-testid="update-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUpdateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-update-btn">
                Update Call
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallHistory;
